import { Navbar } from "../components/Navbar";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { 
  Search, UserPlus, MessageSquare, Briefcase, CheckCircle, 
  Building2, Users, Wrench, ArrowRight, CreditCard
} from "lucide-react";

export default function HowItWorksPage() {
  const steps = {
    candidate: [
      { icon: UserPlus, title: "Crea il tuo profilo", desc: "Registrati gratuitamente e inserisci le tue competenze, esperienze e disponibilità." },
      { icon: Search, title: "Cerca lavoro", desc: "Sfoglia gli annunci delle aziende ticinesi filtrando per settore, comune e tipo di permesso." },
      { icon: CreditCard, title: "Attiva l'abbonamento", desc: "Per candidarti e contattare le aziende, attiva l'abbonamento a soli CHF 12/anno." },
      { icon: MessageSquare, title: "Candidati e chatta", desc: "Invia la tua candidatura e comunica direttamente con le aziende tramite la chat interna." },
      { icon: CheckCircle, title: "Ottieni il lavoro", desc: "Se l'azienda è interessata, condividi i tuoi contatti e organizza il colloquio." }
    ],
    company: [
      { icon: Building2, title: "Registra l'azienda", desc: "Crea un account aziendale gratuito inserendo i dati della tua ditta." },
      { icon: Briefcase, title: "Pubblica annunci", desc: "Crea annunci di lavoro dettagliati specificando requisiti, mansioni e sede." },
      { icon: Search, title: "Cerca candidati", desc: "Sfoglia i profili dei candidati e trova il talento giusto per la tua azienda." },
      { icon: MessageSquare, title: "Contatta i candidati", desc: "Avvia una conversazione con i candidati interessanti tramite la chat interna." },
      { icon: CheckCircle, title: "Assumi", desc: "Organizza i colloqui e trova il collaboratore perfetto per il tuo team." }
    ],
    provider: [
      { icon: UserPlus, title: "Registrati", desc: "Crea un account come fornitore di servizi inserendo i tuoi dati professionali." },
      { icon: CreditCard, title: "Attiva l'abbonamento", desc: "Abbonati a CHF 24/anno per pubblicare i tuoi annunci e ricevere contatti." },
      { icon: Wrench, title: "Pubblica servizi", desc: "Crea annunci per i tuoi servizi con descrizione, tariffe e zona di copertura." },
      { icon: MessageSquare, title: "Ricevi richieste", desc: "I clienti interessati ti contatteranno tramite la chat interna della piattaforma." },
      { icon: CheckCircle, title: "Trova clienti", desc: "Rispondi alle richieste, condividi i tuoi contatti e inizia a lavorare." }
    ]
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      
      <div className="max-w-5xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900">Come funziona</h1>
          <p className="text-slate-500 mt-3 max-w-xl mx-auto">
            LavoroTicino.ch connette candidati, aziende e fornitori di servizi in Canton Ticino. 
            Ecco come iniziare.
          </p>
        </div>

        {/* For Candidates */}
        <section className="mb-16">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-[#DA291C]/10 rounded-xl flex items-center justify-center">
              <Users className="w-6 h-6 text-[#DA291C]" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-slate-900">Per chi cerca lavoro</h2>
              <p className="text-slate-500 text-sm">Trova il lavoro giusto in 5 semplici passi</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {steps.candidate.map((step, i) => (
              <div key={i} className="relative">
                <div className="bg-white rounded-xl border p-5 h-full">
                  <div className="w-10 h-10 bg-[#DA291C]/10 rounded-lg flex items-center justify-center mb-4">
                    <step.icon className="w-5 h-5 text-[#DA291C]" />
                  </div>
                  <span className="text-xs font-bold text-[#DA291C]">PASSO {i + 1}</span>
                  <h3 className="font-bold text-slate-900 mt-1 mb-2">{step.title}</h3>
                  <p className="text-sm text-slate-600">{step.desc}</p>
                </div>
                {i < 4 && (
                  <ArrowRight className="hidden md:block absolute -right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300 z-10" />
                )}
              </div>
            ))}
          </div>

          <div className="text-center mt-6">
            <Link to="/register?type=candidate">
              <Button className="bg-[#DA291C] hover:bg-[#B91C1C]">
                Inizia a cercare lavoro
              </Button>
            </Link>
          </div>
        </section>

        {/* For Companies */}
        <section className="mb-16">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-[#004E64]/10 rounded-xl flex items-center justify-center">
              <Building2 className="w-6 h-6 text-[#004E64]" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-slate-900">Per le aziende</h2>
              <p className="text-slate-500 text-sm">Pubblica annunci GRATUITAMENTE e trova talenti</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {steps.company.map((step, i) => (
              <div key={i} className="relative">
                <div className="bg-white rounded-xl border p-5 h-full">
                  <div className="w-10 h-10 bg-[#004E64]/10 rounded-lg flex items-center justify-center mb-4">
                    <step.icon className="w-5 h-5 text-[#004E64]" />
                  </div>
                  <span className="text-xs font-bold text-[#004E64]">PASSO {i + 1}</span>
                  <h3 className="font-bold text-slate-900 mt-1 mb-2">{step.title}</h3>
                  <p className="text-sm text-slate-600">{step.desc}</p>
                </div>
                {i < 4 && (
                  <ArrowRight className="hidden md:block absolute -right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300 z-10" />
                )}
              </div>
            ))}
          </div>

          <div className="text-center mt-6">
            <Link to="/register?type=company">
              <Button className="bg-[#004E64] hover:bg-[#003B4D]">
                Registra la tua azienda gratis
              </Button>
            </Link>
          </div>
        </section>

        {/* For Providers */}
        <section className="mb-16">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
              <Wrench className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-slate-900">Per chi offre servizi</h2>
              <p className="text-slate-500 text-sm">Trova nuovi clienti pubblicando i tuoi servizi</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {steps.provider.map((step, i) => (
              <div key={i} className="relative">
                <div className="bg-white rounded-xl border p-5 h-full">
                  <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center mb-4">
                    <step.icon className="w-5 h-5 text-emerald-600" />
                  </div>
                  <span className="text-xs font-bold text-emerald-600">PASSO {i + 1}</span>
                  <h3 className="font-bold text-slate-900 mt-1 mb-2">{step.title}</h3>
                  <p className="text-sm text-slate-600">{step.desc}</p>
                </div>
                {i < 4 && (
                  <ArrowRight className="hidden md:block absolute -right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300 z-10" />
                )}
              </div>
            ))}
          </div>

          <div className="text-center mt-6">
            <Link to="/register?type=service_provider">
              <Button className="bg-emerald-600 hover:bg-emerald-700">
                Inizia a offrire servizi
              </Button>
            </Link>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="bg-white rounded-2xl border p-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-6 text-center">Domande frequenti</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { q: "È davvero gratuito per le aziende?", a: "Sì! Le aziende possono pubblicare annunci di lavoro illimitati senza alcun costo." },
              { q: "Quanto costa l'abbonamento candidati?", a: "CHF 24/anno, con lo sconto lancio del 50% paghi solo CHF 12/anno." },
              { q: "Posso cancellare il mio account?", a: "Sì, puoi cancellare il tuo account in qualsiasi momento dalle impostazioni." },
              { q: "Come funziona la chat interna?", a: "La chat permette di comunicare direttamente senza condividere subito i propri contatti." },
              { q: "Verificate le aziende?", a: "Non effettuiamo verifiche. Raccomandiamo di fare le proprie ricerche prima di accordi." },
              { q: "In quali zone operate?", a: "Operiamo sull'intero territorio del Canton Ticino." }
            ].map((faq, i) => (
              <div key={i} className="border-b pb-4">
                <h3 className="font-semibold text-slate-900 mb-2">{faq.q}</h3>
                <p className="text-sm text-slate-600">{faq.a}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-8">
            <p className="text-slate-500 text-sm mb-3">Hai altre domande?</p>
            <Link to="/contact">
              <Button variant="outline">Contattaci</Button>
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
}
